import store from "./store.js";
import router from "./router.js";
import { computed } from "vue";
import { normalizeRightTimestamp } from "./timestamps.js";
import { groupBy, intersection, unique, map_dropNulls } from "./utility.js";

export function changeSettings(selectedCells, getSubmitValues, onCompletion) {
  let sessions = unique(selectedCells.map(cell => cell.componentArgs.tag.$session));
  sessions.forEach(session => {
    let sessionCells = selectedCells.filter(cell => cell.componentArgs.tag.$session == session);
    let submitData = sessionCells.flatMap(cell => getSubmitValues(cell));
    sessionCells.forEach(cell => cell.componentArgs.tag.committed = false);
    session.changeSettings(submitData, () => {
      sessionCells.forEach(cell => cell.componentArgs.tag.committed = true);
      onCompletion?.();
    })
  });
}

export function toggleOptionalSettings(selectedCells, value) {
  let sessions = unique(selectedCells.map(cell => cell.componentArgs.tag.$session));
  sessions.forEach(session => {
    let sessionCells = selectedCells.filter(cell => cell.componentArgs.tag.$session == session);
    let submitData = sessionCells.map(cell => ({ handle: cell.componentArgs.tag.handle, value }));
    sessionCells.forEach(cell => cell.componentArgs.tag.committed = false);
    session.toggleOptionalSettings(submitData, () => {
      sessionCells.forEach(cell => cell.componentArgs.tag.committed = true);
    });
  });
}

export function getBoardId(macAddress) {
  return macAddress.slice(9).replace(/:/g, "").toUpperCase();
}

export function getBoardHostname(board, forceIPv4)
{
  let thisSystem = store.sessions[0].system;
  
  // Include only alphanumeric characters from the board/site name when constructing the hostname.
  let boardName = board.boardName.value.replace(/[^\w\d]/g, "").toLowerCase();
  let siteName = thisSystem.siteName.value.replace(/[^\w\d]/g, "").toLowerCase();

  // Use the gormancontrols.com URL if connected over the relay.
  if (location.hostname.includes(".gormancontrols.com"))
    return `${boardName}.${siteName}.gormancontrols.com`;

  // Use the gormancontrols.com URL if the board is on another network.
  if (board.location.value == "remote")
    return `${boardName}.${siteName}.gormancontrols.com`;

  // Just use the hostname from the page URL if this is the board associated with that URL.
  if (board.boardId.value == "localhost")
    return window.location.host;
  
  // Use a .local hostname or ip address if connected over LAN and this is a secondary board.
  let hasRoutableIPv4 = board.ipAddress.value != "" && !board.ipAddress.value.startsWith("169.");
  let hasIPv6 = board.ipAddressV6.value != "";
  let useIPv6 = !forceIPv4 && hasIPv6;
  let useDotLocalHostname = !forceIPv4 && thisSystem.useMdnsAddressing.value && board.programName.value == "mvv2" && (hasRoutableIPv4 || !useIPv6);
  let portSpecifier = (board.httpPort.value == 80) ? '' : `:${board.httpPort.value}`;
  if (useDotLocalHostname)
    return `${boardName}-${siteName}.local${portSpecifier}`;
  else if (useIPv6)
    return `[${board.ipAddressV6.value}]${portSpecifier}`;
  else
    return `${board.ipAddress.value}${portSpecifier}`;
}

let clickCount = 0;
let clickTime = null;
export function handleAdvancedToggleClick() {
  // Show the advanced menu and settings if clicked 7 times in 4 seconds.
  let now = new Date().getTime();
  clickTime ??= now;
  let elapsed = now - clickTime;
  if (elapsed > 4000) {
    clickCount = 1;
    clickTime = now;
  }
  else {
    clickCount += 1;
    if (clickCount == 7) {
      store.showAdvanced = !store.showAdvanced;
      clickCount = 0;
      clickTime = null;
    }
  }
};

export function openHistory(tag) {
  let system = tag.$session.system;
  let bin = intersection(tag.$parents(), system.bins.children)[0] || system;
  let nowTimestamp = normalizeRightTimestamp(system.utcTime.value);
  let range = { begin: nowTimestamp - 3600, end: nowTimestamp };
  let urlPath = `/records?tagId=${bin.databaseId}&tagId=${tag.databaseId}&begin=${range.begin}&end=${range.end}`;
  if (system == store.sessions[0].system) {
    router.push(urlPath);
  }
  else {
    let boardsOnSite = store.sessions[0].system.boardsOnSite.children;
    let board = boardsOnSite.find(board => system.macAddr.value == board.macAddress.value);
    let hostname = getBoardHostname(board);
    window.location = `http://${hostname}${urlPath}`;
  }
}

export function channelPrefix(channelType) {
  let prefixes = { ani: "SAI", ano: "SAO", gpi: "SDI", gpo: "SDO" };
  let prefix = prefixes[channelType];
  return prefix;
}

export function channelLabel(portNumber, channelNumber, channelType) {
  let channelsPerPort = (channelType == "ano") ? 12 : 24;
  let labelledNumber = portNumber * channelsPerPort + channelNumber + 1;
  return `${channelPrefix(channelType)}-${labelledNumber}`;
}

export function controlChannelLabel(control) {
  if (!control) 
    return "";

  if (control.sensors) 
    return controlChannelLabel(control.sensors.children[0]);

  // Is channel unassigned?
  if (control.channelNumber.value == null)
    return "";

  let string = "";
  if (control.boardId.value != "localhost") {
    string += `${control.boardName.value}: `;
  }
  string += channelLabel(
    control.portNumber.value, 
    control.channelNumber.value, 
    control.controlType.value
  );
  return string;
}

export function useBin(tag) {
  let system = computed(() => {
    return tag.$session.system;
  });
  let bin = computed(() => {
    let bins = system.value.bins.children;
    let bin = tag.$parents().find(parent => bins.includes(parent));
    return bin;
  });
  return {
    system,
    bin
  };
}

export function createAggregateAlarmRows(aggr, unit, selectedCells) {
  let alarmRows = [
    { id: "high", rowName: "High alarm", tag: aggr.highAlarm },
    { id: "low", rowName: "Low alarm", tag: aggr.lowAlarm },
    { id: "commError", rowName: "Comm error", tag: aggr.commErrorAlarmTripped, condition: aggr.commErrorAlarmTripped.value },
    { id: "rangeError", rowName: "Range error", tag: aggr.rangeErrorAlarmTripped, condition: aggr.rangeErrorAlarmTripped.value }
  ];
  let rows = alarmRows
    .filter(row => row.condition ?? true)
    .map(rowData => ({
      id: rowData.id,
      rowName: rowData.rowName,
      cells: [{
        componentType: "AggregateAlarm",
        componentArgs: {
          tag: rowData.tag,
          unit,
          popoverTitle: rowData.rowName
        }
      }]
    }));

  let showCheckAlarmsWhenNotRunningSetting = shouldShowCheckAlarmsWhenNotRunningSetting(aggr);
  if (showCheckAlarmsWhenNotRunningSetting) {
    let helpText = (() => {
      let system = aggr.$session.system;
      let boardsOnSite = system.boardsOnSite.children;
      let bins = aggr.$session.system.bins.children;
      let bin = aggr.$parents().find(parent => bins.includes(parent));
      let aggrName = aggr.$fullName(parent => parent != bin).replace(" - ", " ").toLowerCase();
      if (bin || boardsOnSite.length == 1 && bins.length == 1)
        return `Configures whether ${aggrName} alarms are checked when the bin is not running.`;
      else if (boardsOnSite.length == 1 && bins.length == 2)
        return `Configures whether ${aggrName} alarms are checked when neither bin is running.`;
      else if (boardsOnSite.length == 1)
        return `Configures whether ${aggrName} alarms are checked when no bins are running.`
      else if (bins.length == 2)
        return `Configures whether ${aggrName} alarms are checked when neither of this system's bins are running.`
      else
        return `Configures whether ${aggrName} alarms are checked when none of this system's bins are running.`
    })();
    
    rows.push({
      id: "checkAlarmsWhenNotRunning",
      rowName: "Check alarms when not running",
      cells: [{
        componentType: "ListSetting",
        componentArgs: {
          tag: aggr.checkAlarmsWhenNotRunning,
          popoverTitle: "Check alarms when not running",
          selectedCells,
          helpText,
          options: [
            { caption: "Disabled", value: false },
            { caption: "Enabled", value: true },
          ]
        }
      }]
    });
  }

  return rows;
}

export function shouldShowCheckAlarmsWhenNotRunningSetting(aggr) {
  return aggr.highAlarm.enabled.value || aggr.lowAlarm.enabled.value;
}

export function createChannelMap(sessions, system) {
  let tags = system.$allChildren();
  let controllerServerTags = system.controller.$allChildren();
  
  // Controls belonging to this system.
  let thisSystem = system;
  let ownControls = tags.filter(tag => 
    tag.channelNumber &&
    !controllerServerTags.includes(tag)
  ).map(control => {
    let aggr = control.$parent?.$parent;
    let bins = system.bins.children;
    let nameFilter = tag => !bins.includes(tag) || bins.length > 1;
    return {
      handle: control.handle,
      clientName: thisSystem.boardName.value,
      clientMacAddress: thisSystem.macAddr.value,
      controlName: control.$fullName(nameFilter),
      controlType: control.controlType.value,
      boardId: control.boardId.value,
      boardName: control.boardName.value,
      portNumber: control.portNumber.value,
      channelNumber: control.channelNumber.value,
      sensorType: aggr?.sensorType?.value
    };
  });
  
  // Controls belonging to other systems.
  let systems = map_dropNulls(sessions, session => session.system);
  let peerControls = systems.flatMap(system => {
    let clients = system.controller.clients.children.filter(client => client.macAddress.value != thisSystem.macAddr.value);
    return clients.flatMap(client => {
      let controlGroups = [
        { key: "ani_controls", controlType: "ani" },
        { key: "ano_controls", controlType: "ano" },
        { key: "gpi_controls", controlType: "gpi" },
        { key: "gpo_controls", controlType: "gpo" }
      ];
      return controlGroups.flatMap(controlGroup => {
        let controls = client[controlGroup.key].children;
        return controls.map(control => ({
          handle: control.handle,
          clientName: client.boardName.value,
          clientMacAddress: client.macAddress.value,
          controlName: control.controlName.value,
          controlType: controlGroup.controlType,
          boardId: (system.macAddr.value == thisSystem.macAddr.value) ? "localhost" : getBoardId(system.macAddr.value),
          boardName: system.boardName.value,
          portNumber: control.portNumber.value,
          channelNumber: control.channelNumber.value,
          sensorType: control.sensorType.value
        }));
      });
    });
  });

  // Create an empty channel map.
  const channelsPerPort = 24;
  let channelMap = new Array(channelsPerPort).fill([]);
  
  // Update each element in the map that corresponds to an assigned channel.
  let allControls = [ ... ownControls, ... peerControls ];
  let allAssigned = allControls.filter(control => control.channelNumber != null);
  Object.assign(channelMap, groupBy(allAssigned, control => control.channelNumber));
  
  return channelMap;
}

export function isAssigned(control) {
  if (control.sensors) {
    return control.sensors.children.some(
      sensor => sensor.channelNumber.value != null
    );
  }
  else {
    return control.channelNumber.value != null;
  }
}

export function getControls(channelMap, controlType, boardId, portNumber, channelNumber) {
  let controls = channelMap[channelNumber];
  return controls.filter(control => 
    control.controlType == controlType &&
    control.boardId == boardId &&
    control.portNumber == portNumber
  );
}

export function controlLabel(control) {
  let controlName = control.controlName.split(" - ").join(" ");
  if (control.clientMacAddress == store.sessions[0].system.macAddr.value)
    return controlName;
  else
    return `${control.clientName}: ${controlName}`;
}

export function ordinalLabel(index) {
  let suffix = null;
  let ordinalIndex = index + 1;
  switch (ordinalIndex) {
    case 1: suffix = "st"; break;
    case 2: suffix = "nd"; break;
    case 3: suffix = "rd"; break;
    default: suffix = "th"; break;
  }
  return `${ordinalIndex}<sup>${suffix}</sup>`;
}

export function isThisBoard(board) {
  let system = store.sessions[0].system;
  return board.macAddress.value == system.macAddr.value;
}

export function stripePattern(color1, color2) {
  let documentStyle = getComputedStyle(document.body);
  let color1_value = documentStyle.getPropertyValue(color1.slice(4, -1)).trim();
  let color2_value = documentStyle.getPropertyValue(color2.slice(4, -1)).trim();
  let svgString = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'>` + 
      `<defs>` + 
        `<pattern id='p' width='100' height='100' patternUnits='userSpaceOnUse' patternTransform='rotate(330) scale(0.1)'>` + 
          `<path id='a' stroke='${color1_value}' stroke-width='50' d='M49-1h2v102h-2z'>` +
          `</path>` + 
        `</pattern>` + `
      </defs>` +
      `<rect fill='url(#p)' width='100%' height='100%'>` +
      `</rect>` + 
    `</svg>`
  );
  return { 
    background: color2_value,
    backgroundImage: `url("data:image/svg+xml,${svgString}")`
  };
}